import _ from 'lodash';
import {
  AllegroCreateOrUpdateImpliedWarrantyRequest,
  AllegroFullImpliedWarranty,
  AllegroImpliedWarrantyAddress,
  AllegroImpliedWarrantyPeriod,
} from '../../../api/allegro/impliedwarranty/impliedWarrantyTypes';
import type { ExcelRow } from '../../excel/types';
import {
  createAllegroImpliedWarranty,
  updateAllegroImpliedWarranty,
} from '../../../api/allegro/impliedwarranty/impliedWarranty';
import parseExcel from '../../excel/parseExcel';
import COLUMNS from './impliedWarrantyExcelSchema';
import { showErrorMessage, showSuccessMessage } from '../../../utils/message';
import {
  extractErrorPayload, isAllegroUnauthorized, readString, runTasksSequentially,
} from '../utils';

type UploadTask = () => Promise<AllegroFullImpliedWarranty>;

function mapPeriod(row: ExcelRow, key: string): AllegroImpliedWarrantyPeriod | undefined {
  const period = readString(row, key);

  if (!period) {
    return undefined;
  }

  return { period };
}

function mapAddress(row: ExcelRow): AllegroImpliedWarrantyAddress | undefined {
  const address = {
    name: readString(row, COLUMNS.name),
    street: readString(row, COLUMNS.street),
    postCode: readString(row, COLUMNS.postCode),
    city: readString(row, COLUMNS.city),
    countryCode: readString(row, COLUMNS.countryCode),
  };

  if (_.every(address, _.isUndefined)) {
    return undefined;
  }

  return address as AllegroImpliedWarrantyAddress;
}

function mapRequest(row: ExcelRow): AllegroCreateOrUpdateImpliedWarrantyRequest {
  return {
    name: readString(row, COLUMNS.label),
    individual: mapPeriod(row, COLUMNS.individualPeriod),
    corporate: mapPeriod(row, COLUMNS.corporatePeriod),
    address: mapAddress(row),
    description: readString(row, COLUMNS.description),
  } as AllegroCreateOrUpdateImpliedWarrantyRequest;
}

function prepareUploadTasks(marketplaceCode: string, rows: ExcelRow[]): UploadTask[] {
  return rows.map((row) => {
    const id = readString(row, COLUMNS.id);

    if (id) {
      return () => updateAllegroImpliedWarranty(marketplaceCode, id, mapRequest(row));
    }

    return () => createAllegroImpliedWarranty(marketplaceCode, mapRequest(row));
  });
}

export default async function uploadImpliedWarrantiesExcel(
  marketplaceCode: string,
  file: File,
): Promise<void> {
  const rows = await parseExcel(file);
  const tasks = prepareUploadTasks(marketplaceCode, rows);
  const errors: any[] = [];

  await runTasksSequentially(
    tasks,
    (reason) => {
      if (isAllegroUnauthorized(reason)) {
        return;
      }

      errors.push(extractErrorPayload(reason));
    },
  );

  if (errors.length === 0) {
    showSuccessMessage('Complaints terms successfully uploaded.');
  } else {
    showErrorMessage(JSON.stringify(errors));
  }
}
